import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { DetectiveIcon, SpinnerGapIcon, UsersThreeIcon, ChartLineUpIcon, ArrowRightIcon, PlusCircleIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchCabalRadar, trackCabal } from "../lib/api";
import { markCabalSeen } from "../lib/useCabalAlerts";
import { getClientId } from "../lib/clientId";
import { useAuth } from "../context/AuthContext";
import { QuickBuyLink } from "../components/TradeButton";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const ROSE = "#FB7185";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const timeAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const LoadCard = ({ l }) => {
  const clientId = getClientId();
  const { requireAuth } = useAuth();
  const track = useMutation({
    mutationFn: () => trackCabal(clientId, l.clusterId),
    onSuccess: (d) => {
      if (d.added > 0) {
        toast.success(`Tracking ${d.added} cabal wallet${d.added === 1 ? "" : "s"}`, {
          description: `${d.skipped ? `${d.skipped} already tracked. ` : ""}${d.capped ? `${d.capped} skipped (${d.maxWallets}-wallet cap). ` : ""}Watch them on Smart Money & Wallet Buys.`,
        });
      } else if (d.skipped > 0 && d.capped === 0) {
        toast.info("You're already tracking this whole cabal 🕵️");
      } else if (d.capped > 0) {
        toast.error(`Wallet cap reached (${d.maxWallets}). Remove some on Smart Money to add this cabal.`);
      }
    },
    onError: () => toast.error("Couldn't track this cabal — it may no longer be active."),
  });
  const done = track.isSuccess && track.data?.added > 0;
  return (
    <div data-testid={`cabal-load-${l.tokenMint}`} className="border border-[#FB7185]/30 bg-[#FB7185]/[0.05] p-4 sm:p-5 space-y-3 hover:border-[#FB7185]/60 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center border border-[#FB7185]/50 text-[#FB7185] shrink-0">
          <DetectiveIcon size={20} weight="fill" />
        </div>
        {l.tokenImage ? (
          <img src={l.tokenImage} alt="" className="w-10 h-10 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/coin/${l.tokenMint}`} className="font-heading font-bold text-lg hover:text-neon transition-colors">${l.tokenSymbol || "token"}</Link>
            <span className="flex items-center gap-1 font-mono text-xs px-2 py-0.5 bg-[#FB7185]/15 border border-[#FB7185]/50 text-[#FB7185] font-bold animate-pulse">
              <UsersThreeIcon size={13} weight="bold" /> {l.membersIn} cabal wallets in
            </span>
            {l.winRate != null && (
              <span data-testid={`cabal-winrate-${l.tokenMint}`} title="How this cabal's recent measurable loads have performed (win = ≥1.5x from their entry)"
                className="font-mono text-xs px-2 py-0.5 border font-bold"
                style={l.winRate >= 50
                  ? { background: "rgba(0,229,153,0.1)", borderColor: "rgba(0,229,153,0.45)", color: "#00E599" }
                  : { background: "rgba(245,166,35,0.1)", borderColor: "rgba(245,166,35,0.45)", color: "#F5A623" }}>
                🎯 {l.winRate}% win{l.avgMultiple ? ` · ${l.avgMultiple}× avg` : ""}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">
            From a {l.cabalSize}-wallet cabal · moved together across {l.sharedTokenCount} tokens · {timeAgo(l.lastTimestamp)}
          </div>
        </div>
        <div className="shrink-0">
          <QuickBuyLink coin={{ address: l.tokenMint, url: l.url || `https://dexscreener.com/solana/${l.tokenMint}` }} testid={`cabal-buy-${l.tokenMint}`} />
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm font-mono bg-black/30 border border-white/5 px-3 py-2.5">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Market cap</div>
          <div className="text-white font-semibold">{l.marketCap ? fmtUsd(l.marketCap) : "—"}</div>
        </div>
        {l.score != null && (
          <div className="ml-auto text-center">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Degen Score</div>
            <div className="font-bold text-base" style={{ color: ROSE }}>{l.score}</div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(l.wallets || []).map((w) => (
          <span key={w} className="text-[11px] font-mono px-2 py-1 border bg-[#FB7185]/10 border-[#FB7185]/30 text-[#FB7185]">🕵️ {short(w)}</span>
        ))}
      </div>

      <Link to={`/cabal/${l.clusterId}`} data-testid={`cabal-profile-link-${l.tokenMint}`}
        className="flex items-center justify-center gap-1.5 w-full py-1.5 text-xs font-semibold text-[#FB7185] hover:text-white transition-colors">
        <DetectiveIcon size={13} weight="bold" /> View cabal profile & track record <ArrowRightIcon size={12} />
      </Link>

      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid={`cabal-track-${l.tokenMint}`}
          onClick={() => { if (requireAuth("track this cabal")) track.mutate(); }}
          disabled={track.isPending || done}
          className="flex items-center justify-center gap-2 py-2 text-sm font-semibold border transition-colors disabled:opacity-70 border-[#FB7185]/50 bg-[#FB7185]/[0.08] text-[#FB7185] hover:bg-[#FB7185]/15"
        >
          {track.isPending ? <SpinnerGapIcon size={16} className="animate-spin" />
            : done ? <><CheckCircleIcon size={16} weight="bold" /> Cabal tracked</>
            : <><PlusCircleIcon size={16} weight="bold" /> Track this cabal</>}
        </button>
        <Link to={`/coin/${l.tokenMint}`} data-testid={`cabal-details-${l.tokenMint}`} className="flex items-center justify-center gap-2 py-2 text-sm font-semibold border border-white/15 text-muted-foreground hover:text-neon hover:border-neon/40 transition-colors">
          <ChartLineUpIcon size={16} weight="bold" /> Chart & Scan
        </Link>
      </div>
    </div>
  );
};

export default function CabalRadar() {
  useSeo({
    title: "🕵️ Cabal Radar — Coordinated Wallet Groups | AlphaApeing",
    description: "Watch standing cabals (wallets that repeatedly move together) load up on fresh tokens in real time — get in with the crew, not after them.",
  });
  const { data, isLoading } = useQuery({
    queryKey: ["cabalRadar"],
    queryFn: fetchCabalRadar,
    refetchInterval: 30000,
  });
  const loads = data?.loads || [];

  useEffect(() => {
    if (data) markCabalSeen(loads.map((l) => l.tokenMint));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="cabal-radar-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: ROSE }}>
            <DetectiveIcon size={24} weight="fill" />
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            CABAL <span style={{ color: ROSE }}>RADAR</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          AlphaApeing learns <span style={{ color: ROSE }} className="font-semibold">standing cabals</span> — clusters of wallets that keep buying the same
          tokens together across many launches. When 3+ members of a known cabal ape a fresh coin, it surfaces here live.
          Get in <span className="text-white font-semibold">with</span> the crew, not after them. Turn on the Telegram ping on the{" "}
          <Link to="/alerts" className="text-neon hover:underline">Alerts page</Link>. <span className="text-muted-foreground/70">Solana only.</span>
        </p>
      </div>

      {data && (
        <div className="font-mono text-[11px] text-muted-foreground flex items-center gap-3" data-testid="cabal-stats">
          <span><b style={{ color: ROSE }}>{data.cabalCount}</b> cabals mapped</span>
          <span>·</span>
          <span>{data.trackedWallets} coordinated wallets</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={32} className="animate-spin" style={{ color: ROSE }} /></div>
      ) : loads.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-3">
          <DetectiveIcon size={30} style={{ color: ROSE }} />
          No cabals actively loading right now. A card lights up here the moment 3+ wallets from a known coordinated group pile into the same fresh token.
          <Link to="/smart-money" className="text-neon hover:underline">Track more Smart Money wallets to widen the net →</Link>
        </div>
      ) : (
        <>
          <div className="text-xs font-mono text-muted-foreground flex items-center gap-1">{loads.length} cabal{loads.length === 1 ? "" : "s"} loading now <ArrowRightIcon size={12} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loads.map((l) => <LoadCard key={`${l.clusterId}-${l.tokenMint}`} l={l} />)}
          </div>
        </>
      )}

      <div className="flex items-start gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <DetectiveIcon size={13} style={{ color: ROSE }} className="mt-0.5" />
        Cabals are inferred from co-buying patterns across tracked wallets — a heuristic, not proof of collusion. Always DYOR.
      </div>
    </div>
  );
}
